// src/api/support.ts - UPDATED with centralized ID handling
import { api } from './client';
import { ensureStringId } from './helpers';
import type { ApiResponse, SupportTicket, PaginationData, Order } from '../types';
import { formatDisplayId, isNewFormatId } from '../utils/idGenerator';

export const supportAPI = {
  health: () => api.get<ApiResponse>('/api/support/health'),
  
  createTicket: (data: {
    orderId?: string;
    issueType: 'payment' | 'dispute' | 'rider_issue' | 'customer_issue' | 'technical' | 'safety' | 'other';
    description: string;
    subject?: string;
    priority?: 'low' | 'medium' | 'high' | 'urgent';
    attachments?: string[];
  }) => {
    const payload = {
      ...data,
      orderId: data.orderId ? ensureStringId(data.orderId) : undefined
    };
    return api.post<ApiResponse<{ ticket: SupportTicket }>>('/api/support/tickets', payload);
  },
  
  getMyTickets: (params?: { status?: string; page?: number; limit?: number }) =>
    api.get<ApiResponse<{ tickets: SupportTicket[]; count: number; pagination?: PaginationData }>>('/api/support/tickets/my', { params }),
  
  getTicket: (ticketId: string) => {
    const idString = ensureStringId(ticketId);
    return api.get<ApiResponse<{ ticket: SupportTicket }>>(`/api/support/tickets/${idString}`);
  },
  
  addMessage: (ticketId: string, data: { message: string; attachments?: string[] }) => {
    const idString = ensureStringId(ticketId);
    return api.post<ApiResponse<{ message: any }>>(`/api/support/tickets/${idString}/messages`, data);
  },
  
  uploadAttachment: (formData: FormData) =>
    api.post<ApiResponse<{ fileUrl: string }>>('/api/support/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),
  
  // ========== NEW: Support-only ticket management ==========
  
  getAllTickets: (params?: { 
    status?: string; 
    priority?: string;
    limit?: number; 
    page?: number;
    assignedTo?: string;
  }) => api.get<ApiResponse<{ tickets: SupportTicket[]; count: number; pagination: PaginationData }>>('/api/support/tickets', { params }),
  
  updateTicket: (ticketId: string, data: Partial<SupportTicket>) => {
    const idString = ensureStringId(ticketId);
    return api.put<ApiResponse<{ ticket: SupportTicket }>>(`/api/support/tickets/${idString}`, data);
  },
  
  assignTicket: (ticketId: string, data: { agentId: string }) => {
    const idString = ensureStringId(ticketId);
    return api.post<ApiResponse<{ ticket: SupportTicket }>>(`/api/support/tickets/${idString}/assign`, data);
  },
  
  resolveTicket: (ticketId: string, data: { resolution: string }) => {
    const idString = ensureStringId(ticketId);
    return api.post<ApiResponse<{ ticket: SupportTicket }>>(`/api/support/tickets/${idString}/resolve`, data);
  },
  
  closeTicket: (ticketId: string) => {
    const idString = ensureStringId(ticketId);
    return api.post<ApiResponse<{ ticket: SupportTicket }>>(`/api/support/tickets/${idString}/close`);
  },
  
  // ========== NEW: Support-only order endpoints ==========
  
  getAllOrders: (params?: {
    page?: number;
    limit?: number;
    status?: string;
    serviceType?: string;
    startDate?: string;
    endDate?: string;
  }) => api.get<ApiResponse<{ orders: Order[]; pagination: PaginationData }>>('/api/support/orders', { params }),
  
  getOrderById: (orderId: string) => {
    const idString = ensureStringId(orderId);
    return api.get<ApiResponse<{ order: Order }>>(`/api/support/orders/${idString}`);
  },
  
  // ========== Helper function to format ticket ID for display ==========
  formatTicketId: (ticketId: string): string => {
    return formatDisplayId(ticketId);
  },
  
  // ========== Helper to check if ticket ID is in new format ==========
  isNewFormat: (ticketId: string): boolean => {
    return isNewFormatId(ticketId);
  }
};

export default supportAPI;